import React from 'react';
import useIntl from 'react-intl/src/components/useIntl';
import { Form, Input, Select } from 'semantic-ui-react';
import messages from 'src/messages';
import { ChoicesEditor } from './ChoicesEditor';
import { NumberFieldSettings } from './NumberFieldSettings';
import {
  choicesPatch,
  type CustomFieldKind,
  type KindSettingsProps,
} from './fieldDefinitions';

const toOptions = (choices: string[]) =>
  choices.map((c) => ({ key: c, value: c, text: c }));

function DefaultValueInput({ draft, onChange }: KindSettingsProps) {
  const { formatMessage } = useIntl();

  return (
    <Form.Field>
      <label>{formatMessage(messages.customFieldDefaultValue)}</label>
      <Input
        value={draft.defaultText}
        onChange={(e) => onChange({ defaultText: e.target.value })}
      />
    </Form.Field>
  );
}

function TextFieldSettings(props: KindSettingsProps) {
  return <DefaultValueInput {...props} />;
}

function ChoiceFieldSettings(props: KindSettingsProps) {
  const { draft, onChange } = props;

  return (
    <>
      <DefaultValueInput {...props} />
      <ChoicesEditor
        choices={draft.choices}
        onChange={(next) => onChange(choicesPatch(next, draft))}
      />
    </>
  );
}

function ListFieldSettings({ draft, onChange }: KindSettingsProps) {
  const { formatMessage } = useIntl();

  return (
    <>
      <ChoicesEditor
        choices={draft.choices}
        onChange={(next) => onChange(choicesPatch(next, draft))}
      />
      <Form.Field>
        <label>{formatMessage(messages.customFieldDefaultValue)}</label>
        <Select
          multiple
          options={toOptions(draft.choices)}
          value={draft.listDefault}
          onChange={(_, d) => onChange({ listDefault: d.value as string[] })}
          placeholder={formatMessage(messages.customFieldNone)}
        />
      </Form.Field>
    </>
  );
}

function BooleanFieldSettings({ draft, onChange }: KindSettingsProps) {
  const { formatMessage } = useIntl();

  const options = [
    { key: 'yes', value: true, text: formatMessage(messages.customFieldYes) },
    { key: 'no', value: false, text: formatMessage(messages.customFieldNo) },
  ];

  return (
    <Form.Field>
      <label>{formatMessage(messages.customFieldDefaultValue)}</label>
      <Select
        options={options}
        value={draft.boolDefault}
        onChange={(_, d) => onChange({ boolDefault: d.value as boolean })}
      />
    </Form.Field>
  );
}

export const KIND_SETTINGS: Record<
  CustomFieldKind,
  React.ComponentType<KindSettingsProps>
> = {
  text: TextFieldSettings,
  number: NumberFieldSettings,
  choice: ChoiceFieldSettings,
  list: ListFieldSettings,
  boolean: BooleanFieldSettings,
};
